import React from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../theme';
import ScrollReveal, { useInView, useCountUp, FloatingShape, StaggerReveal } from './ScrollReveal';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const countUp = keyframes`
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const AboutSection = styled.section`
  padding: 6rem 2rem;
  background: ${theme.colors.neutral[50]};
  background-image: 
    radial-gradient(at 0% 0%, ${theme.colors.primary[50]} 0px, transparent 50%),
    radial-gradient(at 100% 100%, ${theme.colors.secondary[50]} 0px, transparent 50%);
  scroll-margin-top: 80px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    padding: 4.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 3.5rem 1.25rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0;
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: clamp(2rem, 4vw, 3rem);
  font-family: ${theme.typography.fontFamily.heading};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.neutral[900]};
  margin-bottom: 1rem;
  position: relative;
  animation: ${fadeInUp} 0.6s ease-out;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: ${theme.gradients.professional};
    border-radius: ${theme.borderRadius.full};
  }
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.neutral[600]};
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
    margin-bottom: 2.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: ${theme.borderRadius.xl};
  text-align: center;
  box-shadow: ${theme.shadows.md};
  border: 1px solid ${theme.colors.neutral[200]};
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform ${theme.transitions.slow};
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.xl};
    border-color: ${theme.colors.primary[300]};
    
    &::before {
      transform: scaleX(1);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const StatNumber = styled.div`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  background: ${theme.gradients.professional};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
  animation: ${countUp} 0.6s ease-out;

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSize['3xl']};
  }

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize['2xl']};
  }
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.neutral[600]};
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  position: relative;
  z-index: 1;
  margin-bottom: 4rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  @media (max-width: 768px) {
    gap: 2.5rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    gap: 2rem;
    margin-bottom: 2.5rem;
  }
`;

const TextSection = styled.div`
  animation: ${fadeInLeft} 0.6s ease-out 0.3s both;
`;

const SectionHeading = styled.h3`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.neutral[900]};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  &::before {
    content: '';
    width: 4px;
    height: 30px;
    background: ${theme.gradients.professional};
    border-radius: ${theme.borderRadius.full};
  }
`;

const Text = styled.div`
  p {
    font-size: ${theme.typography.fontSize.base};
    line-height: ${theme.typography.lineHeight.relaxed};
    color: ${theme.colors.neutral[700]};
    margin-bottom: 1.5rem;
    position: relative;
    padding-left: 1.5rem;
    
    &::before {
      content: '▹';
      position: absolute;
      left: 0;
      color: ${theme.colors.primary[500]};
      font-weight: ${theme.typography.fontWeight.bold};
      font-size: ${theme.typography.fontSize.xl};
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const QuickFacts = styled.div`
  background: white;
  padding: 2rem;
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.md};
  border: 1px solid ${theme.colors.neutral[200]};
  margin-top: 2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-top: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    margin-top: 1.25rem;
  }
`;

const FactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${theme.colors.neutral[100]};
  
  &:last-child {
    border-bottom: none;
  }
  
  svg, span:first-child {
    font-size: ${theme.typography.fontSize.xl};
    min-width: 24px;
  }

  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 0.625rem 0;
  }
`;

const FactLabel = styled.span`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.neutral[900]};
  font-size: ${theme.typography.fontSize.sm};
  min-width: 100px;

  @media (max-width: 480px) {
    min-width: auto;
    width: 100%;
    }
`;

const FactValue = styled.span`
  color: ${theme.colors.neutral[600]};
  font-size: ${theme.typography.fontSize.sm};
  flex: 1;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2rem;
  padding: 1rem 2rem;
  background: ${theme.gradients.professional};
  color: white;
  text-decoration: none;
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  transition: all ${theme.transitions.base};
  box-shadow: ${theme.shadows.md};
  position: relative;
  overflow: hidden;
  min-height: 44px;
  touch-action: manipulation;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};
    
    &::before {
      transform: translateX(100%);
    }
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1.5rem;
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const SkillsSection = styled.div`
  animation: ${fadeInRight} 0.6s ease-out 0.3s both;
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SkillCategory = styled.div`
  background: white;
  padding: 2rem;
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.md};
  border: 1px solid ${theme.colors.neutral[200]};
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform ${theme.transitions.slow};
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${theme.shadows.xl};
    border-color: ${theme.colors.primary[300]};
    
    &::before {
      transform: scaleX(1);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const CategoryTitle = styled.h4`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.neutral[900]};
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: ${theme.typography.fontWeight.bold};
  
  span:first-child {
    font-size: ${theme.typography.fontSize['2xl']};
  }
`;

const SkillGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 0.625rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
`;

const Skill = styled.div`
  background: ${theme.colors.neutral[50]};
  padding: 0.75rem 1rem;
  border-radius: ${theme.borderRadius.lg};
  text-align: center;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.neutral[700]};
  border: 1px solid ${theme.colors.neutral[200]};
  transition: all ${theme.transitions.base};
  font-size: ${theme.typography.fontSize.sm};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${theme.gradients.professional};
    opacity: 0;
    transition: opacity ${theme.transitions.base};
  }
  
  &::after {
    content: attr(data-skill);
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: ${theme.typography.fontWeight.semibold};
    opacity: 0;
    transition: opacity ${theme.transitions.base};
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    border-color: transparent;
    color: transparent;
    
    &::before, &::after {
      opacity: 1;
    }
  }
`;

const InterestsSection = styled.div`
  margin-top: 4rem;
  animation: ${fadeInUp} 0.6s ease-out 0.4s both;
`;

const InterestsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1.25rem;
  }
`;

const InterestCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.md};
  border: 1px solid ${theme.colors.neutral[200]};
  text-align: center;
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.xl};
    border-color: ${theme.colors.primary[300]};
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const InterestIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const InterestTitle = styled.h4`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.neutral[900]};
  margin-bottom: 0.5rem;
`;

const InterestDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.neutral[600]};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

// Animated counter component for stats
const AnimatedStat = ({ number, label }) => {
  const numericPart = parseInt(number);
  const suffix = number.replace(/[0-9]/g, '');
  const [ref, count] = useCountUp(numericPart, 2000);
  
  return (
    <StatCard ref={ref}>
      <StatNumber>{isNaN(numericPart) ? number : `${count}${suffix}`}</StatNumber>
      <StatLabel>{label}</StatLabel>
    </StatCard>
  );
};

function About() {
  const skills = {
    'AI & Machine Learning': {
      icon: '🧠',
      skills: ['Computer Vision', 'Deep Learning', 'TensorFlow', 'OpenCV', 'NLP', 'Edge AI']
    },
    'Robotics': {
      icon: '🤖',
      skills: ['ROS/ROS2', 'Motion Control', 'Sensor Fusion', 'Path Planning', 'SLAM', 'Autonomous Systems']
    },
    'Embedded Systems & IoT': {
      icon: '🔧',
      skills: ['Arduino', 'Raspberry Pi', 'ESP32', 'C/C++', 'IoT Protocols', 'PCB Design']
    },
    'Mobile Development': {
      icon: '📱',
      skills: ['Flutter', 'Dart', 'Firebase', 'REST APIs', 'State Management', 'Mobile UI/UX']
    },
    'Web Development': {
      icon: '🌐',
      skills: ['React', 'Node.js', 'JavaScript', 'HTML/CSS', 'REST APIs']
    },
    'Leadership & Tools': {
      icon: '⚙️',
      skills: ['Git', 'Linux', 'Python', 'Team Leadership', 'Project Management', 'Agile']
    }
  };

  const interests = [
    {
      icon: '♟️',
      title: 'Chess',
      description: 'Competitive chess player with strategic thinking and analytical skills'
    },
    {
      icon: '🥊',
      title: 'Kickboxing',
      description: 'Martial arts training for discipline, focus, and physical fitness'
    },
    {
      icon: '🥋',
      title: 'Taekwondo',
      description: 'Traditional martial art developing resilience and mental strength'
    }
  ];

  const stats = [
    { number: '7+', label: 'Projects Completed' },
    { number: '10+', label: 'Certifications' },
    { number: '2+', label: 'Years Experience' },
    { number: '100%', label: 'Dedication' }
  ];

  const quickFacts = [
    { icon: '🎓', label: 'Education', value: 'Computer Engineering Degree' },
    { icon: '📍', label: 'Location', value: 'Mahdia, Tunisia' },
    { icon: '💼', label: 'Role', value: 'CEO & Founder of KaTEK' },
    { icon: '🌟', label: 'Specialty', value: 'Robotics, IoT & AI' }
  ];

  return (
    <AboutSection id="about">
      {/* Floating decorative shapes */}
      <FloatingShape $top="5%" $left="3%" $size="80px" $shape="rounded" $gradient="linear-gradient(135deg, rgba(59,130,246,0.06), rgba(16,185,129,0.06))" $duration="15s" $rotation="45deg" />
      <FloatingShape $top="40%" $right="2%" $size="60px" $shape="circle" $gradient="linear-gradient(135deg, rgba(217,70,239,0.06), rgba(59,130,246,0.06))" $duration="12s" $delay="3s" />
      <FloatingShape $bottom="15%" $left="5%" $size="50px" $shape="rounded" $gradient="linear-gradient(135deg, rgba(245,158,11,0.06), rgba(59,130,246,0.06))" $duration="18s" $delay="5s" $rotation="20deg" />
      
      <Container>
        <ScrollReveal direction="blur" duration="1s">
          <Title>About Me</Title>
          <Subtitle>
            Building the future with Robotics, AI & Intelligent Systems
          </Subtitle>
        </ScrollReveal>

        {/* Stats Section */}
        <StaggerReveal direction="bounce" stagger={0.15} baseDelay={0.1}>
          <StatsContainer>
            {stats.map((stat, index) => (
              <AnimatedStat key={index} number={stat.number} label={stat.label} />
            ))}
          </StatsContainer>
        </StaggerReveal>

        {/* Main Content */}
        <Content>
          <ScrollReveal direction="slideRotate" delay="0.1s" duration="0.9s">
          <TextSection>
            <SectionHeading>My Story</SectionHeading>
        <Text>
          <p>
                I'm a Computer Engineer and the CEO & Founder of KaTEK, specializing in Robotics, IoT, Embedded Systems, and AI-driven solutions. I combine deep technical expertise with entrepreneurial vision to design, build, and lead the development of intelligent systems that make a real-world impact.
          </p>
          <p>
                My journey started with mobile development using Flutter, building sleek, high-performance applications. I then expanded into embedded technologies, robotics, and artificial intelligence — working with microcontrollers, sensors, ROS, and machine learning to create autonomous, intelligent solutions. This passion led me to found KaTEK, where I lead a team focused on pushing the boundaries of robotics and AI.
          </p>
          <p>
                At KaTEK, I specialize in developing connected systems that bridge mobile applications, IoT devices, AI models, and robotic platforms. Whether it's architecting autonomous vehicles, deploying edge AI solutions, or integrating smart devices, I thrive on solving complex challenges and transforming innovative ideas into impactful products.
          </p>
        </Text>

            {/* Quick Facts */}
            <QuickFacts>
              {quickFacts.map((fact, index) => (
                <FactItem key={index}>
                  <span>{fact.icon}</span>
                  <FactLabel>{fact.label}:</FactLabel>
                  <FactValue>{fact.value}</FactValue>
                </FactItem>
              ))}
            </QuickFacts>

            <CTAButton href="/CV.pdf" target="_blank" rel="noopener noreferrer">
              📄 Download Resume
            </CTAButton>
          </TextSection>
          </ScrollReveal>

          <ScrollReveal direction="flipY" delay="0.2s" duration="0.9s">
          <SkillsSection>
            <SectionHeading>Technical Skills</SectionHeading>
          <SkillsContainer>
              {Object.entries(skills).map(([category, data]) => (
              <SkillCategory key={category}>
                  <CategoryTitle>
                    <span>{data.icon}</span>
                    {category}
                  </CategoryTitle>
                <SkillGrid>
                    {data.skills.map((skill, index) => (
                      <Skill key={index} data-skill={skill}>
                        {skill}
                      </Skill>
                  ))}
                </SkillGrid>
              </SkillCategory>
            ))}
          </SkillsContainer>
          </SkillsSection>
          </ScrollReveal>
        </Content>

        {/* Personal Interests */}
        <ScrollReveal direction="spring" delay="0.1s" duration="1s">
        <InterestsSection>
          <SectionHeading style={{ justifyContent: 'center' }}>Beyond Engineering</SectionHeading>
          <InterestsGrid>
            {interests.map((interest, index) => (
              <InterestCard key={index}>
                <InterestIcon>{interest.icon}</InterestIcon>
                <InterestTitle>{interest.title}</InterestTitle>
                <InterestDescription>{interest.description}</InterestDescription>
              </InterestCard>
            ))}
          </InterestsGrid>
        </InterestsSection>
        </ScrollReveal>
      </Container>
    </AboutSection>
  );
}

export default About;
